// Import
import { useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import {
  InputCst,
  SelectCst,
  AutocompleteCst,
  MultiSelectCst,
  RadioCst,
  DataPickerCst,
  SingleCheckboxCst,
  InputAssociationChipsCst,
  MultiTextCst,
  EuroTextCst,
} from "@components/InputCst";
import { ColumnInterface } from "@components/Table/types";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import BackgroundImageCst from "@components/BackgroundImageCst";
import { defaultValidation } from "@utils/utilsValidators";
import ButtonCst from "@components/ButtonCst";
import DownloaderUploaderCst from "@components/DownloaderUploaderCst";
import { useNavigate, useParams } from "react-router-dom";
import InputChipsCst from "@components/InputCst/InpuctChipsCst";
import AssociationLibraryCst from "@components/AssociationLibraryCst";
import AssociationTableCst from "@components/AssociationTableCst";
import { PagesID } from "@utils/utilsConfigurations";
import TimePickerCst from "@components/InputCst/TimePickerCst";
import { TextEditorCst } from "@components/TextEditorCst";
import { Association } from "src/interfaces/Common";
import { Divider } from "@components/TimeSlotCst/style";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { useAuth } from "@context/auth";
import dayjs from "dayjs";

// Interface
interface DetailCostFormInterface {
  row?: any;
  columns: ColumnInterface[];
  additionalColumns?: ColumnInterface[];
  actionsForForms?: ColumnInterface[];
  onSubmit: (
    inputForm: any,
    fileNotificationsAsset: any,
    fileTemplateAsset: any,
    participationsAsset: any,
    uploadTheme: any,
    imagePath: any,
    pdfTemplatePath: any,
    image: any,
    document: any,
    fileUploaded: { [x: string]: any },
  ) => void;
  onReset?: () => void;
  formActionType?: string[];
  dragDrop?: boolean;
  inputsException?: (col: any, inputForm: any) => any;
  backButton?: () => any;
  getValues?: (accessorKey: string) => any;
  handleDelete?: (accessorKey: string, element: any) => void;
  handleAssociation?: (accessorKey: string, value: any) => void;
  setOpenAssociation?: (accessorKey: string, open: boolean) => void;
  resetContractAssociations: () => void;
  association?: boolean | Association;
  isFaculties?: boolean;
  disabledConfig?: { view: string; accessoryKey: string };
  readOnly?: boolean;
  isCorporate?: boolean;
  deleteDisabled?: boolean;
  options?: any;
  LoCounter?: boolean;
  LoCountValue?: number;
  LoCountMaxSelection?: number;
  optionsSelect?: any;
  getOptionValue?: any;
  durationKey?: boolean; // valore da settare se vogliamo dare al form anche la DURATA dell'elemento selezionato nella grid
  fullWidth?: boolean;
  supplierAssociation?: any;
  setContractAssociation?: any;
  setSupplierAssociation?: any;
  isNew?: boolean;
  isAccounting?: boolean;
}

// eslint-disable-next-line no-empty-pattern
const DetailCostFormCst: React.FC<DetailCostFormInterface> = ({
  row,
  columns,
  actionsForForms = [],
  dragDrop,
  inputsException,
  onSubmit,
  onReset,
  backButton,
  getValues,
  handleDelete,
  handleAssociation,
  setOpenAssociation,
  resetContractAssociations,
  association,
  readOnly,
  LoCounter,
  LoCountValue,
  LoCountMaxSelection,
  optionsSelect,
  durationKey,
  fullWidth = false,
  supplierAssociation,
  setContractAssociation,
  setSupplierAssociation,
  isNew = false,
  isAccounting = false,
}) => {
  const { idCostModel, idProject, id } = useParams();
  const theme = useTheme();
  const { t } = useTranslation(["form"]);
  const { t: tCost } = useTranslation(["projects-cost-detail"]);
  const navigate = useNavigate();
  const [inputForm, setInputForm] = React.useState<any>({});
  const [errorInputForm, setErrorInputForm] = React.useState<any>({});
  const [fileUploaded, setFileUploaded] = React.useState<any>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [formLoaded, setFormLoaded] = useState(isNew);
  const [showModalWarning, setShowModalWarning] = useState(false);
  const { user } = useAuth();

  /*########### ROLE PERMISSION ###########*/

  /*Permission to indetify PM*/
  const isPM = user?.permissions.find(
    (permission: any) =>
      permission === "usermgmt.budget.project.mark.as.pending.activation",
  );

  /*Permission to add first form fields*/
  const addFirstSection = user?.permissions.find(
    (permission: any) =>
      permission === "usermgmt.budget.goods.receipt.add.first.section",
  );

  /*Permission to update all fields*/
  const updateAll = user?.permissions.find(
    (permission: any) => permission === "usermgmt.budget.cost.update.all",
  );

  /*#######################################*/

  const getInitialData = () => {
    let initialState: any = {};
    columns?.forEach(({ accessorKey }) => {
      switch (accessorKey) {
        case "supplier":
          if (row.supplier) {
            row.supplier.label = `${row.supplier.business_name} - ${row.supplier.sap_code}`;
            setSupplierAssociation && setSupplierAssociation([row.supplier]);
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "contract":
          if (row.contract != null) {
            row.contract.label = row.contract.sap_code;
            setContractAssociation && setContractAssociation([row.contract]);
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "project":
          if (row.info_project != null) {
            row.project = `${row.info_project.code || ""} - ${row.info_project.title || ""}`;
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "project_manager":
          if (row.info_project != null) {
            row.project_manager = `${row.info_project.project_manager.first_name || ""} ${row.info_project.project_manager.last_name || ""}`;
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "project_budget":
          if (row.info_project != null) {
            row.project_budget = row.info_project.allocated_budget || null;
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "financing_plan":
          if (row.info_project != null) {
            row.financing_plan =
              row.info_project?.financing_plan?.title || null;
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "project_cost_center":
          if (row.info_project != null) {
            row.project_cost_center = `${row.info_project.cost_center.sap_code || ""} - ${row.info_project.cost_center.description || ""}`;
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "cost_model_item_budget":
          if (row.info_cost_model != null) {
            row.cost_model_item_budget =
              row.info_cost_model.allocated_budget || null;
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "cost_model_item_account":
          if (row.info_cost_model != null) {
            row.cost_model_item_account = `${row.info_cost_model.account.sap_number || ""} - ${row.info_cost_model.account_description.title || ""}`;
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "status":
          if (isNew) {
            row.status = "DRAFT";
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "insert_date":
          initialState = {
            ...initialState,
            [accessorKey]: isNew ? dayjs() : row[accessorKey] || null,
          };
          break;
        default:
          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
      }
    });
    return initialState;
  };

  const resetInputForm = () => {
    let initialState = {};
    onReset && onReset();

    resetContractAssociations();
    if (row) {
      setInputForm(getInitialData());
    } else {
      setInputForm(initialState);
    }

    setIsBlocked(false);
  };

  useEffect(() => {
    if (
      row &&
      Object.keys(row).length > 0 &&
      columns?.length > 0 &&
      Object.keys(inputForm).length === 0
    ) {
      const initialState = getInitialData();
      setInputForm(initialState);
      setFormLoaded(true);

      if (isPM && row.info_project.status === "CLOSED") {
        readOnly = true;
      }

      // Disable all columns if readOnly=true prop is passed
      if (readOnly) {
        columns?.forEach((column) => (column.disabled = true));
      }
    }
  }, [row, columns]);

  //TODO: refactor validation v.1
  const onConfirm = () => {
    // FIXME: defaultValidation non effettua validazione de i campi upload (es. upload image certificati anche se required non viene validato).
    // fix predisposta in branch fix/actionsForFormsValidation
    const { canBeSubmit, showError } = defaultValidation(columns, inputForm);

    /*Show error if the total cost is grather than the maximum allocatable budget od the cost model*/
    const max_cost_value =
      row?.info_cost_model?.residual_budget + (row?.total_cost ?? 0);
    if (inputForm.total_cost > max_cost_value) {
      setShowModalWarning(true);
      return;
    }

    setErrorInputForm(showError);

    // in base alla presenza del error nel col:descrizione errore,  vado a settare un messaggio di errore nel file config delle traduzioni
    // ES. viene utilizzata per le associazioni sezione Gestione Finanziata
    let stringError = "";
    const findErrorCol = columns.map((el) => {
      if (el.error) {
        stringError = el.error;
        return {
          [el.accessorKey]: {
            msg: el.error,
          },
        };
      } else {
        return;
      }
    });
    //logica errore sul col.error settata nella callback inputsException vedi Gestione Finanziata associazioni o sezione PIANI data inizio e data fine
    const mergeErrorArray = Object.assign({}, ...findErrorCol);
    if (Object.keys(mergeErrorArray).length > 0) {
      setErrorInputForm({ ...showError, ...mergeErrorArray });
    }
    //logica errore sul col.error settata nella callback inputsException END

    if (canBeSubmit) {
      setIsBlocked(false);
      onSubmit(
        inputForm,
        fileUploaded && fileUploaded["uploadNotification"],
        fileUploaded && fileUploaded["uploadTemplate"],
        fileUploaded && fileUploaded["participations"],
        fileUploaded && fileUploaded["uploadTheme"],
        fileUploaded && fileUploaded["imagePath"],
        fileUploaded && fileUploaded["pdfTemplatePath"],
        fileUploaded && fileUploaded["image"],
        fileUploaded && fileUploaded["document"],
        fileUploaded,
      );
    }
  };

  const getErrMsg = (accessorKey: string): string => {
    return errorInputForm[accessorKey]?.msg || errorInputForm[accessorKey];
  };

  const minLength = (accessorKey: string): number => {
    return errorInputForm[accessorKey]?.value;
  };

  const maxLength = (accessorKey: string): number => {
    return errorInputForm[accessorKey]?.value;
  };

  const getInputsList = (col: any, inputForm: any) => {
    if (inputsException) {
      col = inputsException(col, inputForm);
    }

    switch (col.type) {
      case "association": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={col.fullWidth ? 12 : 6}>
            <InputAssociationChipsCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={getValues && getValues(col.accessorKey)}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              noAssociationButton={col.disableAssociationButton}
              disabled={col.disabled}
              textTooltip={col.tooltip}
              required={col.required}
              onDelete={(e: any) => {
                setIsBlocked(true);
                handleDelete && handleDelete(col.accessorKey, e);
              }}
              onRedirect={() => {
                setIsBlocked(true);
                setOpenAssociation && setOpenAssociation(col.accessorKey, true);
              }}
              labelKey={col.labelKey}
            />
          </Grid>
        );
      }
      case "singleCheckbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <SingleCheckboxCst
              key={col.accessorKey}
              id={col.accessorKey}
              value={inputForm[col.accessorKey]}
              label={col.header}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({
                  ...inputForm,
                  ...{ [col.accessorKey]: e.target.checked },
                });
              }}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      case "text": {
        return (
          <Grid
            key={col.accessorKey}
            item
            xs={12}
            sm={col.fullWidth ? 12 : col.sxWidth ? 4 : col.xsWidth ? 2 : 6}
          >
            <InputCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({
                  ...inputForm,
                  ...{ [col.accessorKey]: e.target.value },
                });
              }}
              maxValue={col?.rules?.max?.value}
              multiline={col.multiline}
              rows={col.rows}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
              textTooltip={col.tooltip}
              type={col.inpuType || "text"}
              minWidth={false}
            />
          </Grid>
        );
      }
      case "chips": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <InputChipsCst
              label={col.header}
              id={col.accessorKey}
              value={inputForm[col.accessorKey]}
              required={col.required}
              maxLength={col?.rules?.max?.value}
              disabled={col.disabled}
              textTooltip={col.textTooltip}
              validationType={col.validationType}
              error={errorInputForm[col.accessorKey] ? true : false}
              onAdd={(list: any[]) => {
                setIsBlocked(true);
                setInputForm({
                  ...inputForm,
                  ...{ [col.accessorKey]: list },
                });
              }}
              deleteDisabled={col.deleteDisabled}
              undeletableValues={col.undeletableValues}
            />
          </Grid>
        );
      }
      case "dynamicSelect": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <SelectCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              optionsToDisable={col.optionsToDisable}
              value={inputForm[col.accessorKey]?.value || ""}
              service={col.service}
              required={col.required}
              onChange={(selected: any) => {
                setIsBlocked(true);
                if (col.callBack) {
                  setInputForm(col.callBack(selected, row));
                } else {
                  setInputForm({
                    ...inputForm,
                    ...{
                      [col.accessorKey]: optionsSelect.find(
                        (el: any) => el.value === selected,
                      ),
                    },
                  });
                }
              }}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              options={optionsSelect}
              textTooltip={col.tooltip}
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "dynamicUOGSelect": {
        let error = false;
        const buildLevels = (data: any) => {
          const levels: any = {};

          const rootItem = data.find((el: any) => el.parent_code == "NULL");

          function getLevelItems(parentCode: string, level: number) {
            if (!levels[level]) levels[level] = [];
            const items = data.filter(
              (item: any) => item.parent_code === parentCode,
            );

            if (items.length === 0) return;

            levels[level].push(...items);

            items.forEach((item: any) => getLevelItems(item.code, level + 1));
          }

          if (rootItem) {
            error = false;
            getLevelItems(rootItem.code, 1);
          } else {
            error = true;
          }

          const fullLevels = {
            "0": [rootItem],
            ...levels,
          };

          const options = Object.keys(fullLevels)
            .filter((levelIndex) => fullLevels[levelIndex].length > 0)
            .map((levelIndex) => ({
              name: `${levelIndex}`,
              value: levelIndex,
              label: `${levelIndex}`,
            }));

          return options;
        };

        const optionChoice = buildLevels(optionsSelect);

        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <SelectCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              optionsToDisable={col.optionsToDisable}
              value={inputForm[col.accessorKey]}
              service={col.service}
              required={col.required}
              onChange={(selected: any) => {
                setIsBlocked(true);
                if (col.callBack) {
                  setInputForm(col.callBack(selected, row));
                } else {
                  setInputForm({
                    ...inputForm,
                    ...{ [col.accessorKey]: selected },
                  });
                }
              }}
              options={optionChoice}
              textTooltip={col.tooltip}
              error={!!errorInputForm[col.accessorKey] || !!error}
              errorMessage={
                error
                  ? t("error.field.uog-hierarchy") || ""
                  : t("error.field.required") || ""
              }
              disabled={error || col.disabled}
            />
          </Grid>
        );
      }
      case "select": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <SelectCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              optionsToDisable={col.optionsToDisable}
              value={inputForm[col.accessorKey]} //REFACTOR temp to show right value on select
              service={col.service}
              required={col.required}
              onChange={(e: any) => {
                setIsBlocked(true);
                if (col.callBack) {
                  setInputForm(col.callBack(e, row));
                } else {
                  setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
                }
              }}
              options={col?.options}
              textTooltip={col.tooltip}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "select-checkbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <MultiSelectCst
              id={col.accessorKey}
              label={col.header}
              optionsKey={col.optionsKey}
              value={inputForm[col.accessorKey]}
              optionsToDisable={col.optionsToDisable}
              disabled={col.disabled}
              onChange={(e: any) => {
                setIsBlocked(true);
                let newValue = JSON.parse(JSON.stringify(e));
                let newInputForm = JSON.parse(JSON.stringify(inputForm));
                setInputForm({
                  ...newInputForm,
                  ...{ [col.accessorKey]: newValue },
                });
              }}
              options={col.options}
              required={col.required}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      case "autocomplete": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <AutocompleteCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              options={col?.options}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "multiCheckbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <MultiSelectCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              textTooltip={col.tooltip}
              required={col.required}
              service={col.service}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              options={col?.options}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "radio": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <RadioCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              options={col.options}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "dateTime": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DataPickerCst
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              minDate={col.minDate}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "dateTimeYear": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DataPickerCst
              views={["year"]}
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "time": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <TimePickerCst
              id={col.accessorKey}
              value={inputForm[col.accessorKey]}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`) || "Error"
              }
              required={col.required}
              label={col.header}
              disabled={col.disabled}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({ ...inputForm, ...{ [col.accessorKey]: e } });
              }}
            />
          </Grid>
        );
      }
      case "multi-text": {
        return (
          <MultiTextCst
            key={col.accessorKey}
            label={col.header}
            fields={col.fields}
            value={inputForm[col.accessorKey]}
            disabled={col.disabled}
            onChange={(e: any) => {
              setIsBlocked(true);
              setInputForm({
                ...inputForm,
                ...{ [col.accessorKey]: e },
              });
            }}
          />
        );
      }
      case "editor": {
        return (
          <TextEditorCst
            options={col.editorOptions}
            value={inputForm[col.accessorKey]}
            onChange={(e: any) => {
              setIsBlocked(true);
              setInputForm((prev: any) => {
                return {
                  ...prev,
                  [col.accessorKey]: e,
                };
              });
            }}
          />
        );
      }
      case "upload": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DownloaderUploaderCst
              name={col.header}
              accept={col?.accept}
              url={col.service}
              type={col.upload_type}
              onChange={(e: any) => {
                setInputForm({
                  ...inputForm,
                  [col.accessorKey]: {
                    e,
                  },
                });
                setFileUploaded({
                  ...fileUploaded,
                  [col.accessorKey]: {
                    file: e,
                  },
                });
              }}
              fileName={!!row?.originalFileName && row.originalFileName}
              downloadDisabled={!!col.downloadDisabled}
              onDownload={col.downloadCallback && col.downloadCallback}
              onTemplateDownload={col.templateCallback && col.templateCallback}
              onDelete={
                col.deleteCallback &&
                (() => {
                  setFileUploaded(null);
                  return col.deleteCallback && col.deleteCallback();
                })
              }
              disabled={col.disabled}
              required={col?.required}
              showError={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      case "euro-text": {
        return (
          <Grid
            key={col.accessorKey}
            item
            xs={12}
            sm={col.fullWidth ? 12 : col.sxWidth ? 4 : col.xsWidth ? 2 : 6}
          >
            <EuroTextCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={inputForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setIsBlocked(true);
                setInputForm({
                  ...inputForm,
                  ...{ [col.accessorKey]: e.target.value },
                });
              }}
              maxValue={col?.rules?.max?.value}
              multiline={col.multiline}
              rows={col.rows}
              error={errorInputForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
              textTooltip={col.tooltip}
              type={col.inpuType || "text"}
              minWidth={false}
              residue={col.residue || undefined}
            />
          </Grid>
        );
      }
      default:
        return <></>;
    }
  };

  /*Clear all the field of a section*/
  const clearSectionFields = (section: string) => {
    if (!columns || !inputForm) return;

    const keysToClear = columns
      .filter((col: any) => col.section === section)
      .map((col) => col.accessorKey);

    let hasChange = false;
    const updatedForm = { ...inputForm };

    keysToClear.forEach((key) => {
      if (
        updatedForm[key] !== null &&
        updatedForm[key] !== undefined &&
        updatedForm[key] !== ""
      ) {
        updatedForm[key] = null;
        hasChange = true;
      }
    });

    if (hasChange) {
      setInputForm(updatedForm);
    }
  };

  /*Function to only the form fields */
  const getFormField = useMemo(() => {
    const isBC_BE = inputForm.management_type === "BC_BE";

    return columns
      ?.filter(
        (col: any) =>
          col.section === "form" && col.accessorKey !== "insert_date",
      )
      .map((col: any) => {
        /*Contract is enabled when management_type is "BC_BE" and supplierAssociation is not empty */
        if (col.accessorKey === "contract") {
          const hasSupplierAssociation = supplierAssociation?.length > 0;
          const isBCBE = inputForm.management_type === "BC_BE";

          const enabled = hasSupplierAssociation && isBCBE;

          col.disabled = !enabled;
          col.required = enabled;
        }

        /*Status is disabled when creating a new item */
        if (col.accessorKey === "status") {
          col.disabled = isNew || !isAccounting;
          if (isBC_BE) {
            col.optionsToDisable = ["SENT_TO_SAP"];
          }
        }

        /*Insert date is editable only in accounting edit with full permissions*/
        if (col.accessorKey === "insert_date") {
          col.disabled = isNew || !updateAll || !isAccounting;
        }

        /*Total cost can't overflow the residual budget of the cost model*/
        if (col.accessorKey === "total_cost") {
          const max_cost_value =
            (row?.info_cost_model?.residual_budget ?? 0) +
            (row?.total_cost ?? 0);
          col.rules.maxValue.value = max_cost_value;
          col.residue = max_cost_value;
          col.tooltip = `${tCost("max-cost-value")}: €${Number(
            max_cost_value,
          ).toLocaleString("it-IT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        }

        //TODO: SISTEMARE STATO ROW STATUS
        if (!addFirstSection && !updateAll && row && row?.status !== "DRAFT") {
          //if ((!addFirstSection ) && !updateAll) {
          col.disabled = true;
        }

        if (!isNew && isPM && row?.status !== "DRAFT") {
          col.disabled = true;
        }

        return getInputsList(col, inputForm);
      });
  }, [
    columns,
    inputForm,
    errorInputForm,
    t,
    getErrMsg,
    getValues,
    handleDelete,
    maxLength,
    minLength,
    setOpenAssociation,
  ]);

  const getInsertDateField = useMemo(() => {
    const insertDateColumn = columns?.find(
      (col: any) => col.section === "form" && col.accessorKey === "insert_date",
    );

    if (!insertDateColumn) {
      return null;
    }

    const col = { ...insertDateColumn };

    col.disabled = isNew || !updateAll || !isAccounting;

    if (!addFirstSection && !updateAll && row && row?.status !== "DRAFT") {
      col.disabled = true;
    }

    if (!isNew && isPM && row?.status !== "DRAFT") {
      col.disabled = true;
    }

    return getInputsList(col, inputForm);
  }, [
    columns,
    inputForm,
    errorInputForm,
    updateAll,
    isAccounting,
    isNew,
    addFirstSection,
    row,
    isPM,
    t,
    getErrMsg,
    minLength,
    maxLength,
  ]);

  /*Function to get only project field*/
  const getProjectField = useMemo(() => {
    return columns
      ?.filter((col: any) => col.section === "info_project")
      .map((col: any) => {
        return getInputsList(col, inputForm);
      });
  }, [columns, inputForm]);

  /*Function to get only rda field*/
  const getRdaField = useMemo(() => {
    const validStatus = ["SENT_TO_SAP", "ISSUED"].includes(inputForm.status);
    const isBC_BE = inputForm.management_type === "BC_BE";
    const isRDA = inputForm.management_type === "RDA";

    return columns
      ?.filter((col: any) => col.section === "info_rda")
      .map((col: any) => {
        if (col.accessorKey === "fc_release_date" && !isRDA) {
          return null;
        }

        col.disabled = !validStatus || !isAccounting || isBC_BE;
        col.required = validStatus && isAccounting && !isBC_BE;

        if (col.accessorKey === "fc_release_date") {
          col.disabled = !validStatus || !isAccounting || !isRDA;
          col.required = false;
        }

        if (!updateAll) {
          col.disabled = true;
        }

        return getInputsList(col, inputForm);
      });
  }, [columns, inputForm, errorInputForm]);

  /*Function to get only oda field*/
  const getOdatField = useMemo(() => {
    const validStatus = ["ISSUED"].includes(inputForm.status);

    return columns
      ?.filter((col: any) => col.section === "info_oda")
      .map((col: any) => {
        col.disabled = !validStatus || !isAccounting;
        col.required = validStatus && isAccounting;

        if (!updateAll) {
          col.disabled = true;
        }

        return getInputsList(col, inputForm);
      });
  }, [columns, inputForm, errorInputForm]);

  /*Function to get only em field*/
  const getEmField = useMemo(() => {
    return columns
      ?.filter((col: any) => col.section === "info_em")
      .map((col: any) => {
        col.disabled = true;
        return getInputsList(col, inputForm);
      });
  }, [columns, inputForm, errorInputForm]);

  /*Clear the contract association when management_type is not "BC_BE" or supplierAssociation is empty */
  useEffect(() => {
    if (
      formLoaded &&
      ((inputForm &&
        inputForm.management_type &&
        inputForm.management_type !== "BC_BE") ||
        !supplierAssociation ||
        supplierAssociation.length === 0)
    ) {
      setContractAssociation?.([]);
    }
  }, [inputForm.management_type, supplierAssociation]);

  /*Total cost is the product of unit_cost and qt */
  useEffect(() => {
    if (formLoaded && inputForm.unit_cost != null && inputForm.qt != null) {
      const totalCost = Number(inputForm.unit_cost) * Number(inputForm.qt);
      setInputForm((prev: any) => ({
        ...prev,
        total_cost: totalCost,
      }));
    }
  }, [inputForm.unit_cost, inputForm.qt, formLoaded]);

  useEffect(() => {
    if (!["ISSUED"].includes(inputForm.status)) {
      clearSectionFields("info_oda");
    }

    if (!["SENT_TO_SAP", "ISSUED"].includes(inputForm.status)) {
      clearSectionFields("info_rda");
    }
  }, [inputForm]);

  return (
    <>
      {/* <BackButtonWarning isBlocked={isBlocked} /> */}
      {!association && (
        <Grid container>
          <Grid item xs={0} lg={fullWidth ? 0 : 4} xl={fullWidth ? 0 : 3}>
            <BackgroundImageCst
              image="section-internal-form.jpg"
              disableGradient
            />
          </Grid>
          <Grid item xs={12} lg={fullWidth ? 12 : 8} xl={fullWidth ? 12 : 9}>
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                padding: "32px",
                minHeight: "495px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {/*Project info fields*/}
              <Grid container spacing={3}>
                {getProjectField}
              </Grid>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Divider theme={theme} />
              </Box>

              {/*Input form fields*/}
              <Grid container spacing={3}>
                {getFormField}
                {getInsertDateField}
              </Grid>

              {!isNew && (
                <>
                  {/*Rda form fields*/}
                  {inputForm.management_type != "BC_BE" && (
                    <>
                      <Box sx={{ mt: 2, mb: 2 }}>
                        {tCost("request-pm")}
                        <Divider theme={theme} />
                      </Box>

                      <Grid container spacing={3}>
                        {getRdaField}
                      </Grid>
                    </>
                  )}

                  <Box sx={{ mt: 2, mb: 2 }}>
                    {tCost("sent-to-sap")}
                    <Divider theme={theme} />
                  </Box>

                  {/*ODA form fields*/}
                  <Grid container spacing={3}>
                    {getOdatField}
                  </Grid>

                  <Box sx={{ mt: 2, mb: 2 }}>
                    {tCost("issued")}
                    <Divider theme={theme} />
                  </Box>

                  {/*EM form fields*/}
                  <Grid container spacing={3}>
                    {getEmField}
                  </Grid>
                </>
              )}

              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    mt: "32px",
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                    justifyContent: "space-between",
                    [theme.breakpoints.down("sm")]: {
                      flexDirection: "column",
                    },
                  }}
                >
                  <ButtonCst
                    id={"back-button"}
                    variant={"outlined"}
                    size={"medium"}
                    sx={{
                      minWidth: "150px",
                      mr: "auto",
                      [theme.breakpoints.down("lg")]: {
                        minWidth: "100px",
                      },
                      [theme.breakpoints.down("sm")]: {
                        minWidth: "100%",
                      },
                    }}
                    onClick={() => {
                      if (backButton) {
                        backButton();
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    {t("backButton")}
                  </ButtonCst>
                  <ButtonCst
                    id={"reset-button"}
                    variant={"outlined"}
                    size={"medium"}
                    sx={{
                      minWidth: "150px",
                      [theme.breakpoints.down("lg")]: {
                        minWidth: "100px",
                      },
                      [theme.breakpoints.down("sm")]: {
                        minWidth: "100%",
                      },
                    }}
                    onClick={resetInputForm}
                  >
                    {t("resetButton")}
                  </ButtonCst>
                  <ButtonCst
                    id={"save-button"}
                    variant={"contained"}
                    size={"medium"}
                    sx={{
                      minWidth: "150px",
                      [theme.breakpoints.down("lg")]: {
                        minWidth: "100px",
                      },
                      [theme.breakpoints.down("sm")]: {
                        minWidth: "100%",
                      },
                    }}
                    onClick={() => onConfirm()}
                    disabled={!!readOnly}
                  >
                    {t("saveButton")}
                  </ButtonCst>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      )}
      <ModalConfirmsCst
        open={showModalWarning}
        title={tCost("total-cost-warning.title")}
        description={tCost("total-cost-warning.description")}
        routeToSamePage={true}
        removeButtons={true}
        onActionConfirmed={() => {
          setShowModalWarning(false);
        }}
        onCallBackClosed={() => {
          setShowModalWarning(false);
        }}
      />
      {(association as { type: string })?.type === "library" && (
        <AssociationLibraryCst
          association={
            getValues && getValues((association as Association).accessorKey)
          }
          setAssociation={(value: any) => {
            handleAssociation &&
              handleAssociation(
                (association as Association).accessorKey,
                value,
              );
          }}
          setCloseAssociation={(value: boolean) =>
            setOpenAssociation &&
            setOpenAssociation((association as Association).accessorKey, value)
          }
          filters={[
            {
              field: "type",
              value: (association as Association).mediaType,
            },
          ]}
          multiple
          title={t((association as Association).titleKey)}
        />
      )}
      {(association as { type: string })?.type === "table" && (
        <AssociationTableCst
          association={
            getValues && getValues((association as Association).accessorKey)
          }
          setAssociation={(value: any) => {
            handleAssociation &&
              handleAssociation(
                (association as Association).accessorKey,
                value,
              );
            setInputForm({
              ...inputForm,
              ...{ [(association as Association).accessorKey]: value },
            });
          }}
          setCloseAssociation={(value: boolean) =>
            setOpenAssociation &&
            setOpenAssociation((association as Association).accessorKey, value)
          }
          title={t((association as Association).titleKey)}
          service={(association as { service: string }).service}
          pageID={(association as { pageId: PagesID }).pageId}
          filter={(association as Association).accessorKey}
          defaultFilters={(association as Association).defaultFilters}
          manageConfigs={(association as Association).manageConfigs}
          enableMultiRowSelection={(association as Association).multiChoice}
          labelKey={(association as Association).labelKey}
          durationKey={durationKey || false}
        />
      )}
    </>
  );
};

export default DetailCostFormCst;
